import { ArrowRightIcon } from '@heroicons/react/outline'
import Link from 'next/link'
import HomepageBookCallButton, {
    homepageButtonStyles,
} from './HomepageBookCallButton'
import {
    managedCampaignBullets,
    managedCampaignSteps,
    olcBullets,
    olcIntegrations,
    productFormats,
    selfServeBullets,
} from './config'

function BulletList({ items, featured = false }) {
    return (
        <div
            className={`mb-6 border-t pt-4 ${
                featured ? 'border-white/10' : 'border-black/10'
            }`}>
            <div
                className={`mb-3 text-[11px] font-extrabold uppercase tracking-[0.1em] ${
                    featured ? 'text-white/50' : 'text-[#6F7785]'
                }`}>
                Best for
            </div>
            <ul className="space-y-2">
                {items.map((item) => (
                    <li
                        key={item}
                        className={`flex gap-[10px] text-[14.5px] leading-[1.5] ${
                            featured ? 'text-white/80' : 'text-[#4A5160]'
                        }`}>
                        <span
                            className={`flex-shrink-0 font-extrabold ${
                                featured ? 'text-[#FAB331]' : 'text-[#F28230]'
                            }`}>
                            ✓
                        </span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

function ManagedVisual() {
    return (
        <div className="flex flex-col gap-[11px]">
            {managedCampaignSteps.map((step) => (
                <div
                    key={step.number}
                    className="grid items-center gap-[14px] rounded-[14px] border border-black/10 bg-[#FFF9F2] px-[18px] py-4 transition hover:translate-x-[3px] hover:border-[rgba(237,92,47,0.3)] md:grid-cols-[42px_1fr_auto]">
                    <div
                        className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] text-[13px] font-extrabold text-white ${step.tone}`}>
                        {step.number}
                    </div>
                    <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#6F7785]">
                            {step.label}
                        </div>
                        <div className="text-[14.5px] font-bold tracking-[-0.01em] text-[#3F454F]">
                            {step.title}
                        </div>
                    </div>
                    {step.kind === 'chips' && (
                        <div className="flex gap-1">
                            {['High-LTV', 'Lapsed', 'New'].map((chip) => (
                                <span
                                    key={chip}
                                    className="rounded-full border border-black/10 bg-white px-[10px] py-1 text-[10px] font-bold text-[#4A5160]">
                                    {chip}
                                </span>
                            ))}
                        </div>
                    )}
                    {step.kind === 'thumbs' && (
                        <div className="flex gap-1">
                            <div className="h-8 w-6 rounded bg-[linear-gradient(135deg,#ED5C2F,#F28230)]" />
                            <div className="h-8 w-6 rounded bg-[linear-gradient(135deg,#F28230,#FAB331)]" />
                            <div className="relative h-8 w-6 rounded border border-black/10 bg-white">
                                <div className="absolute inset-1 bg-[linear-gradient(0deg,#E5E7EB_33%,transparent_33%,transparent_50%,#E5E7EB_50%,#E5E7EB_83%,transparent_83%)]" />
                            </div>
                        </div>
                    )}
                    {step.kind === 'live' && (
                        <div className="flex items-center gap-[5px] rounded-full bg-[#fab33126] px-[9px] py-1">
                            <span className="homepage-v2-live-pulse h-[5px] w-[5px] rounded-full bg-[#FAB331]" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[#B5821A]">
                                In production
                            </span>
                        </div>
                    )}
                    {step.kind === 'chart' && (
                        <div className="flex h-6 items-end gap-0.5">
                            {[35, 55, 75, 90, 100].map((height) => (
                                <div
                                    key={height}
                                    className="w-[5px] rounded-[1.5px] bg-[linear-gradient(180deg,#ED5C2F,#F28230)]"
                                    style={{ height: `${height}%` }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

function SelfServeVisual() {
    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
            {productFormats.map((format) => (
                <Link key={format.name} href={format.href}>
                    <a className="relative flex flex-col items-center rounded-[14px] border border-black/10 bg-[#FFF9F2] px-[18px] py-[22px] text-center no-underline transition hover:-translate-y-0.5 hover:border-[rgba(237,92,47,0.3)] hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#ED5C2F] focus:ring-offset-2">
                        {format.badge && (
                            <span className="absolute right-2 top-2 rounded-full bg-[rgba(237,92,47,0.12)] px-2 py-0.5 text-[8.5px] font-extrabold uppercase tracking-[0.04em] text-[#ED5C2F]">
                                {format.badge}
                            </span>
                        )}
                        <span
                            className={`mb-3 flex h-[50px] w-[50px] items-center justify-center rounded-[12px] text-[24px] leading-none ${format.tone}`}>
                            {format.icon}
                        </span>
                        <span className="text-[13.5px] font-bold leading-[1.2] tracking-[-0.01em] text-[#3F454F]">
                            {format.name}
                        </span>
                    </a>
                </Link>
            ))}
        </div>
    )
}

function OlcVisual() {
    return (
        <div className="grid overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-12px_rgba(0,0,0,0.5)] md:grid-cols-[150px_1fr]">
            <div className="border-b border-black/10 bg-[#F9F8F6] p-4 md:border-b-0 md:border-r">
                <div className="mb-3 pl-1 text-[9px] font-bold uppercase tracking-[0.07em] text-[#6F7785]">
                    Integrations
                </div>
                <div className="space-y-1">
                    {olcIntegrations.map((integration) => (
                        <div
                            key={integration.name}
                            className="flex items-center gap-2 rounded-lg px-2 py-2 transition hover:bg-white">
                            <span
                                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[9px] font-extrabold leading-none text-white ${integration.badgeClass}`}>
                                {integration.icon}
                            </span>
                            <span className="flex-1 text-[11px] font-semibold leading-[1.2] text-[#3F454F]">
                                {integration.name}
                            </span>
                            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#16A34A]" />
                        </div>
                    ))}
                </div>
            </div>

            <div className="p-5">
                <div className="mb-4 text-sm font-bold tracking-[-0.01em] text-[#3F454F]">
                    Create automation
                </div>

                <div className="space-y-4">
                    <div>
                        <div className="mb-1.5 text-[9px] font-bold uppercase tracking-[0.07em] text-[#6F7785]">
                            Trigger
                        </div>
                        <div className="flex items-center justify-between rounded-[9px] border border-[#ED5C2F] bg-[#FEF3EC] px-3 py-2.5">
                            <div className="flex items-center gap-2">
                                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#FEF3EC] text-xs font-bold text-[#ED5C2F]">
                                    ⚡
                                </span>
                                <span className="text-[12px] font-semibold text-[#3F454F]">
                                    New customer
                                </span>
                            </div>
                            <span className="text-[12px] text-[#6F7785]">
                                ▾
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-center py-1 text-[#6F7785]">
                        <svg
                            width="14"
                            height="14"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            />
                        </svg>
                    </div>

                    <div>
                        <div className="mb-1.5 text-[9px] font-bold uppercase tracking-[0.07em] text-[#6F7785]">
                            Action
                        </div>
                        <div className="flex items-center justify-between rounded-[9px] border border-[#ED5C2F] bg-[#FEF3EC] px-3 py-2.5">
                            <div className="flex items-center gap-2">
                                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#FFF0EB] text-xs font-bold text-[#F28230]">
                                    ✍
                                </span>
                                <span className="text-[12px] font-semibold text-[#3F454F]">
                                    Send Real Penned Letter
                                </span>
                            </div>
                            <span className="text-[12px] text-[#6F7785]">
                                ▾
                            </span>
                        </div>
                    </div>

                    <div className="rounded-full bg-[linear-gradient(90deg,#ED5C2F_0%,#F28230_100%)] px-4 py-2.5 text-center text-[12px] font-bold text-white shadow-[0_4px_12px_-2px_rgba(237,92,47,0.4)]">
                        Save automation
                    </div>
                </div>
            </div>
        </div>
    )
}

function HomepagePathsSection() {
    return (
        <section
            id="how-to-work-with-us"
            className="border-b border-black/10 bg-[#FFF9F2] py-[76px]">
            <div className="container mx-auto px-5 md:px-0">
                <div className="mb-10 max-w-[720px]">
                    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#F28230]">
                        How to work with us
                    </div>
                    <h2 className="mb-3 text-[clamp(26px,3.2vw,44px)] font-extrabold leading-[1.12] tracking-[-0.025em] text-[#3F454F]">
                        <span>One direct mail partner. </span>
                        <span className="olm-gradient-text">
                            Three ways to use OLM.
                        </span>
                    </h2>
                    <p className="max-w-[560px] text-[17px] leading-[1.75] text-[#4A5160]">
                        Whether you need a full campaign partner, an easier way
                        to order mail, or software to integrate direct mail into
                        your tech stack, OLM gives you the services and
                        infrastructure to execute at scale.
                    </p>
                </div>

                <div
                    id="managed-direct-mail-campaigns"
                    className="mb-[22px] grid gap-8 rounded-[22px] border border-black/10 bg-white p-8 transition hover:-translate-y-[3px] hover:border-[rgba(237,92,47,0.2)] hover:shadow-[0_24px_60px_-16px_rgba(63,69,79,0.10)] lg:grid-cols-[1fr_1.1fr] lg:gap-12 lg:p-10">
                    <div>
                        <h3 className="mb-[14px] text-[30px] font-extrabold leading-[1.12] tracking-[-0.025em] text-[#3F454F]">
                            Managed Direct Mail Campaigns
                        </h3>
                        <p className="mb-6 text-base leading-[1.65] text-[#4A5160]">
                            Work with our team to plan, build, and execute
                            direct mail campaigns with the right strategy,
                            audience, creative, format, timing, and production
                            support.
                        </p>
                        <BulletList items={managedCampaignBullets} />
                        <HomepageBookCallButton>
                            Book a strategy call
                        </HomepageBookCallButton>
                    </div>
                    <ManagedVisual />
                </div>

                <div className="mb-[22px] grid gap-8 rounded-[22px] border border-black/10 bg-white p-8 transition hover:-translate-y-[3px] hover:border-[rgba(237,92,47,0.2)] hover:shadow-[0_24px_60px_-16px_rgba(63,69,79,0.10)] lg:grid-cols-[1fr_1.1fr] lg:gap-12 lg:p-10">
                    <div>
                        <h3 className="mb-[14px] text-[30px] font-extrabold leading-[1.12] tracking-[-0.025em] text-[#3F454F]">
                            Self-Serve Ordering
                        </h3>
                        <p className="mb-6 text-base leading-[1.65] text-[#4A5160]">
                            For teams that already know what they want to mail,
                            order postcards, letters, snap packs, self-mailers,
                            handwritten-style mail, and more through a
                            streamlined online experience.
                        </p>
                        <BulletList items={selfServeBullets} />
                        <Link href="/products">
                            <a className={homepageButtonStyles.primary}>
                                <span>Shop products</span>
                                <ArrowRightIcon className="h-4 w-4" />
                            </a>
                        </Link>
                    </div>
                    <SelfServeVisual />
                </div>

                <div className="relative grid gap-8 overflow-hidden rounded-[22px] border border-transparent bg-[#3F454F] p-8 shadow-[0_24px_60px_-16px_rgba(63,69,79,0.4)] lg:grid-cols-[1fr_1.1fr] lg:gap-12 lg:p-10">
                    <div className="pointer-events-none absolute left-0 top-0 h-full w-[5px] bg-[linear-gradient(95deg,#ED5C2F_0%,#F28230_55%,#FAB331_100%)]" />
                    <div className="pointer-events-none absolute right-0 top-0 h-[340px] w-[340px] rounded-full bg-[radial-gradient(circle,rgba(237,92,47,0.20)_0%,transparent_65%)]" />
                    <div className="relative z-10">
                        <h3 className="mb-[14px] text-[30px] font-extrabold leading-[1.12] tracking-[-0.025em] text-white">
                            Open Letter Connect
                        </h3>
                        <p className="mb-6 text-base leading-[1.65] text-white/70">
                            Use OLC to integrate direct mail into your existing
                            tech stack. Trigger personalized mail from your CRM,
                            ecommerce platform, marketing workflows, or internal
                            software.
                        </p>
                        <BulletList items={olcBullets} featured />
                        <a
                            href="https://openletterconnect.com/"
                            target="_blank"
                            rel="noreferrer"
                            className={homepageButtonStyles.primary}>
                            <span>Explore OLC</span>
                            <ArrowRightIcon className="h-4 w-4" />
                        </a>
                    </div>
                    <div className="relative z-10">
                        <OlcVisual />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HomepagePathsSection
